import {
  DEFAULT_STUN_URL,
  acceptsDeliveryAssignment,
  MAX_BUFFER_SEGMENTS,
  MAX_PEER_CONNECTIONS,
  P2P_DEADLINE_MS,
  DEFAULT_SEGMENT_DURATION_MS,
  INIT_SEGMENT_ID,
  SEGMENT_ID_REGEX,
  aesGcmDecrypt,
  canonicalSegmentId,
  importAesKey,
  isSeedPeerId,
  segmentCacheKey,
  segmentAad,
  segmentIdForSeq,
  segmentSequence,
  sha256Hex,
  validPartMetadata,
  playablePartTail,
  trimMediaWindow,
  COMPAT_RENDITION_ID,
  parsePrivateMediaAuthority,
  type PrivateMediaAuthority,
} from '@ohmyprime/p2p-shared';
import type {
  SignalingMessage,
  DeliveryAssignment,
  PeerListPayload,
  PeerRole,
  OfferPayload,
  AnswerPayload,
  IceCandidatePayload,
  SegmentMapPayload,
  PeerStatsPayload,
  InitAnnounce,
  LiveWindowPayload,
  SegmentAnnounce,
  SegmentAnnouncePayload,
  SegmentHoldersPayload,
  StreamEndedPayload,
  SeedExposurePayload,
} from '@ohmyprime/p2p-shared';

import type {
  P2PEngineConfig,
  P2PEngineEventMap,
  P2PEngineMetrics,
  PeerInfo,
  PlaybackState,
} from './types.js';
import type { SegmentRequestSpec } from './segment-scheduler.js';
import { SignalingClient } from './signaling-client.js';
import { PeerConnection } from './peer-connection.js';
import { SegmentStore } from './segment-store.js';
import { SegmentVerifier } from './segment-verifier.js';
import { SegmentScheduler } from './segment-scheduler.js';
import { UploadCapacityWindow } from './upload-capacity.js';
import { MetricsCollector } from './metrics-collector.js';
import { SeedOriginSource, type OriginSource } from './origin-source.js';

const PEER_STATS_INTERVAL_MS = 5_000;
/**
 * An offer this peer sent that has not produced an open DataChannel by then is abandoned and, while
 * the peer is still in the tracker's peer list, sent again. Without it a lost offer or answer (the
 * tracker drops messages above its rate limit) leaves the connection in have-local-offer for good,
 * and the peer is never offered to again.
 */
export const OFFER_TIMEOUT_MS = 8_000;
const SEGMENT_JUMP_RESET_THRESHOLD = 12;
const LIVE_SEGMENT_RETAIN_WINDOW = 18;
/** Upper bound on remembered segment metadata (catalog entries). */
const MAX_SEGMENT_META = 128;
/** Seed: announces re-sent to the tracker after a signaling reconnect. */
const SEED_RECENT_ANNOUNCES = 30;
const DEFAULT_SEED_FIRST_HOP_FANOUT = 3;
const LIVE_WINDOW_MAX_SEGMENTS = 30;
const DEFAULT_MAX_PENDING_UPLOADS = 64;
const DEFAULT_MAX_INFLIGHT_UPLOADS_PER_PEER = 4;

/** Outcome of trying to upload one segment to one peer. */
type UploadOutcome = 'sent' | 'deferred' | 'nacked' | 'duplicate' | 'unavailable';

/** Catalog entry: id + hash are required, the rest of SegmentAnnounce is optional. */
export type SegmentCatalogEntry = Pick<SegmentAnnounce, 'segmentId' | 'sha256'> &
  Partial<Omit<SegmentAnnounce, 'segmentId' | 'sha256'>>;

/** Input of P2PEngine.publishSegment (seed). */
export interface PublishSegmentInput {
  seq: number;
  durationMs: number;
  /** Date.now() at capture of the segment's first frame. */
  capturedAt: number;
  /** `iv || AES-GCM ciphertext` as produced by aesGcmEncrypt. */
  ciphertext: ArrayBuffer;
  /** SHA-256 hex over `ciphertext`. Computed when omitted. */
  sha256?: string;
  /** Key id; defaults to config.keyId. */
  keyId?: string;
  independent?: boolean;
  gopStartSeq?: number;
}

interface SegmentMeta {
  sha256: string;
  keyId?: string;
  capturedAt?: number;
}

interface MetaWaiter {
  resolve: (meta: SegmentMeta) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

type OptionalConfigKeys =
  | 'transportProfile'
  | 'transportCapabilities'
  | 'peerId'
  | 'iceTransportPolicyThreshold'
  | 'signalingToken'
  | 'signalingTokenTs'
  | 'signalingTokenStreamId'
  | 'seedToken'
  | 'sessionId'
  | 'keyId'
  | 'originSource'
  | 'proxyOriginSource'
  | 'getSignalingToken';

type ResolvedConfig = Required<Omit<P2PEngineConfig, OptionalConfigKeys>> &
  Pick<P2PEngineConfig, OptionalConfigKeys>;

export interface RenditionAuthority { id:string; generation:string; protocolVersion:1|2; initSha256:string; codecs:string; }
/** Renditions the backend catalogue can authorize: `compat` is the H.264 lane next to an AV1 `main` (protocol 2). */
const AUTHORIZED_RENDITION_IDS:readonly string[]=['main','240p',COMPAT_RENDITION_ID];
function validateRenditionAuthority(identity:RenditionAuthority):void {
  if(!AUTHORIZED_RENDITION_IDS.includes(identity.id) || !/^[A-Za-z0-9._-]{1,128}$/.test(identity.generation) ||
     !/^[a-f0-9]{64}$/.test(identity.initSha256) || !/^avc1\.[A-Fa-f0-9]{6},mp4a\.40\.2$/.test(identity.codecs) ||
     identity.protocolVersion !== (identity.id==='main'?1:2))throw new Error('Invalid rendition authority');
}
export interface PreparedRendition { authority:RenditionAuthority; window:LiveWindowPayload; entry:SegmentAnnounce; init:ArrayBuffer; media:ArrayBuffer; transferBps?:number;
  /** Last refreshes' media deliveries (bytes, ms from request to delivery), newest last, at most six. */
  samples?:{bytes:number;ms:number;durationMs?:number}[]; }

export class P2PEngine {
  private readonly config: ResolvedConfig;
  private readonly role: PeerRole;
  private readonly peerId: string;

  private readonly signalingClient: SignalingClient;
  private readonly store: SegmentStore;
  private readonly verifier: SegmentVerifier;
  private readonly metrics: MetricsCollector;
  private readonly uploadCapacity = new UploadCapacityWindow();
  private readonly scheduler: SegmentScheduler;
  private readonly seedOrigin: SeedOriginSource | null = null;

  private readonly peers: Map<string, PeerConnection> = new Map();
  private readonly peerInfoMap: Map<string, PeerInfo> = new Map();
  /** Seeds known from tracker roles (peer-list / live-window); `seed-` prefix also counts. */
  private readonly seedPeerIds: Set<string> = new Set();
  private readonly ownSegments: Set<string> = new Set();
  private readonly pendingUploads: Map<string, Set<string>> = new Map();
  private readonly segmentMeta: Map<string, SegmentMeta> = new Map();
  private readonly metaWaiters: Map<string, MetaWaiter[]> = new Map();
  private readonly uploadFetchTriggered: Set<string> = new Set();
  /** Seed: peers that already received the current live-head segment (fan-out cap). */
  private readonly seedLiveHeadServed: Map<string, Set<string>> = new Map();
  /** Seed: live-head requests over the fan-out cap, served on the next publish. */
  private readonly seedDeferred: Map<string, Set<string>> = new Map();
  /** Segments already uploaded to each peer (never send the same one twice). */
  private readonly sentToPeer: Map<string, Set<string>> = new Map();
  /** Key id of the latest announced segments (viewer). */
  private announcedKeyId = '';
  private pendingLegacyInit: InitAnnounce | null = null;
  private readonly recentAnnounces: SegmentAnnounce[] = [];

  private initData: ArrayBuffer | null = null;
  private initAnnounce: InitAnnounce | null = null;
  private initCodecs: string | undefined;
  private liveWindow: LiveWindowPayload | null = null;
  /** Viewer: where origin fetches go, from the tracker's latest live window. */
  private mediaOrigin: 'seed' | 'proxy' = 'seed';
  private privatePublication: PrivateMediaAuthority | null = null;
  private privateLeaseTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly pendingWork = new Set<Promise<unknown>>();
  private closePromise: Promise<void> | null = null;
  private readonly joinWaiters = new Set<() => void>();
  private trackWork<T>(promise: Promise<T>): Promise<T> {
    this.pendingWork.add(promise);
    void promise.then(() => this.pendingWork.delete(promise), () => this.pendingWork.delete(promise));
    return promise;
  }
  private decryption: { keyId: string; key: Promise<CryptoKey> } | null = null;
  private playerReportsLatency = false;
  private segmentsPublished = 0;
  private bytesUploaded = 0;
  private confirmedUploadBytes = 0;
  private confirmedUploadDeliveries = 0;

  private deliveryAssignment: DeliveryAssignment | null = null;
  private joined = false;
  private joinResolved = false;
  private destroyed = false;
  private streamId = '';
  private activeRenditionEngine: P2PEngine | null = null;
  private preparingRenditionEngine: P2PEngine | null = null;
  private preparedRendition: PreparedRendition | null = null;
  private forwardOwner: P2PEngine | null = null;
  private renditionAttempt = 0;

  private renditionId = 'main';
  private renditionAuthority: {id:string;generation:string;protocolVersion:1|2;initSha256:string;codecs:string} | null = null;
  private catalogEpoch = 0;
  private swarmPeerCount = 0;
  private highestSegmentSequence = -1;
  /** Tracker join times (ms) of known peers and of this peer, for senior fetcher election. */
  private readonly peerJoinedAt: Map<string, number> = new Map();
  /** Peers in the tracker's latest peer list (excluding this one). */
  private listedPeerIds: Set<string> = new Set();
  private readonly offerTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private selfJoinedAt: number | undefined;

  private peerStatsTimer: ReturnType<typeof setInterval> | null = null;
  private localUploadRate = 0;
  private localReliability = 1;

  private readonly eventHandlers: {
    [K in keyof P2PEngineEventMap]?: Array<P2PEngineEventMap[K]>;
  } = {};

  constructor(config: P2PEngineConfig) {
    this.role = config.role ?? 'viewer';
    this.config = {
      role: this.role,
      stunUrls: [DEFAULT_STUN_URL],
      turnServers: [],
      maxPeers: MAX_PEER_CONNECTIONS,
      p2pDeadlineMs: P2P_DEADLINE_MS,
      enabled: true,
      region: this.detectRegion(),
      iceTransportPolicy: 'all',
      segmentDurationMs: DEFAULT_SEGMENT_DURATION_MS,
      retainSegments: config.transportProfile ? Math.ceil(18_000 / config.transportProfile.partDurationMs) + 1 : MAX_BUFFER_SEGMENTS,
      seedFirstHopFanout: DEFAULT_SEED_FIRST_HOP_FANOUT,
      fetcherElection: 'senior',
      maxPendingUploads: DEFAULT_MAX_PENDING_UPLOADS,
      maxInflightUploadsPerPeer: DEFAULT_MAX_INFLIGHT_UPLOADS_PER_PEER,
      enableUrgentChannel: true,
      ultraLowLatency: false,
      acceptPeerConnections: true,
      // Explicit `undefined` (e.g. an unset env var) keeps the default.
      ...(Object.fromEntries(
        Object.entries(config).filter(([, value]) => value !== undefined),
      ) as P2PEngineConfig),
    };

    if (config.transportProfile && (!config.keyId || !config.transportCapabilities?.transportVersions.includes(2))) {
      throw new Error('Delivery v2 requires an authenticated generation and verified transport capabilities');
    }
    this.peerId = config.peerId ?? this.generatePeerId();

    this.signalingClient = new SignalingClient(
      this.config.signalingUrl,
      this.peerId,
      this.config.signalingToken ?? this.config.seedToken,
      this.config.signalingTokenTs,
      this.config.signalingTokenStreamId,
      this.role,
      this.config.sessionId,
      {
        getToken: this.config.getSignalingToken,
        onRejected: (info) => this.emit('signaling-rejected', info),
      },
    );

    this.store = new SegmentStore(this.config.retainSegments);
    this.verifier = new SegmentVerifier();
    this.metrics = new MetricsCollector();

    let originSource: OriginSource | undefined = this.config.originSource;
    if (!originSource && this.role === 'viewer') {
      this.seedOrigin = new SeedOriginSource(
        {
          getConnectedSeedIds: () => this.getConnectedSeedIds(),
          requestFromSeed: (seedId, segmentId, urgent) =>
            this.peers.get(seedId)?.requestSegment(segmentId, urgent) ?? false,
        },
        Math.max(3_000, this.config.segmentDurationMs * 4),
      );
      const seedOrigin: OriginSource = this.seedOrigin;
      const proxyOrigin = this.config.proxyOriginSource;
      const selected = () => this.mediaOrigin === 'proxy' && proxyOrigin ? proxyOrigin : seedOrigin;
      originSource = {
        get networkSource() { return selected().networkSource; },
        get networkPath() { return selected().networkPath; },
        fetch: (segmentId, opts) => {
          const origin = selected();
          if (origin === seedOrigin && this.config.transportProfile) this.ensureSeedConnection();
          return origin.fetch(segmentId, opts);
        },
      };
    }

    if (originSource) {
      const source = originSource;
      originSource = {
        get networkSource() { return source.networkSource; },
        get networkPath() { return source.networkPath; },
        fetch: (id, options) => this.trackWork(Promise.resolve().then(() => {
          if (this.destroyed || options.signal.aborted) throw new Error('P2PEngine is destroyed');
          return source.fetch(id, options);
        })),
      };
    }
    this.scheduler = new SegmentScheduler(
      this.store,
      this.verifier,
      this.metrics,
      {
        requestFromPeer: (peerId, segmentId, urgent = true) => {
          const conn = this.peers.get(peerId);
          if (!conn) return false;
          return conn.requestSegment(canonicalSegmentId(segmentId), urgent);
        },
        getPeerConnection: (peerId) => this.peers.get(peerId),
        onSegmentFromPeer: (segmentId, peerId) => {
          this.flushPendingUploads(segmentId);
          this.announceSegmentInternal(segmentId);
          this.emit('segment-from-peer', segmentId, peerId);
        },
        onSegmentFromOrigin: (segmentId) => {
          this.flushPendingUploads(segmentId);
          this.announceSegmentInternal(segmentId);
          this.emit('segment-from-seed', segmentId);
        },
        onDeadlineMiss: () => {
          this.metrics.recordDeadlineMiss();
        },
        getSwarmSize: () => this.swarmPeerCount,
        getConnectedPeerCount: () => this.viewerPeerIds().length,
        getConnectedPeerIds: () => this.viewerPeerIds(),
        getLocalPeerId: () => this.peerId,
        getDeliveryAssignment: () => this.deliveryAssignment,
        getLocalRegion: () => this.config.region,
        onP2PSkip: (reason, segmentId) => {
          this.emit('p2p-skip', reason, segmentId);
        },
        onModeChange: (info) => {
          this.emit('mode-transition', info);
        },
        isSeedPeer: (peerId) => this.isSeed(peerId),
        getExpectedSha256: (segmentId) => this.segmentMeta.get(segmentId)?.sha256,
        getPeerJoinedAt: (peerId) => (peerId === this.peerId ? this.selfJoinedAt : this.peerJoinedAt.get(peerId)),
      },
      this.config.p2pDeadlineMs,
      this.config.enabled,
      this.config.segmentDurationMs,
      this.config.ultraLowLatency,
      { originSource, fetcherElection: this.config.fetcherElection, transportVersion: this.config.transportProfile?.transportVersion },
    );
    if (this.role === 'seed') {
      // The seed is the origin: it never fetches, it only publishes and uploads.
      this.scheduler.setEnabled(false);
    }

    this.metrics.start(() => this.emit('metrics', this.getMetrics()));
    this.registerSignalingHandlers();
    this.startPeerStatsReporting();
  }

  /** Private seed only: reauthorize the existing socket using the existing tracker seed-auth protocol.
   * The host owns proactive OAuth renewal and its getSignalingToken callback supplies this same fresh token on reconnect.
   * This does not extend a media lease, replace a generation or reset delivery progress.
   */
  refreshPrivateSignaling(token: string, ts: number): void {
    const authority = this.privatePublication;
    if (this.destroyed || this.role !== 'seed' || !authority || Date.parse(authority.expiresAt) <= Date.now() ||
        !token || token.length > 16384 || !Number.isSafeInteger(ts) || ts <= 0) throw new Error('Private seed authorization unavailable');
    this.signalingClient.send('seed-auth', undefined, {token, ts, stream: authority.publicationId,
      role: 'seed', renditionId: authority.medium, peerId: this.peerId});
  }

  /** Renew future ICE configuration and allocations; keep sockets, catalog and DataChannels. */
  configureIceServers(servers: RTCIceServer[], policy: RTCIceTransportPolicy = this.config.iceTransportPolicy): void {
    if (this.destroyed) return;
    const stunUrls: string[] = [];
    const turnServers: RTCIceServer[] = [];
    for (const server of servers) {
      const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
      stunUrls.push(...urls.filter(url => /^stuns?:/i.test(url)));
      const relay = urls.filter(url => /^turns?:/i.test(url));
      if (relay.length) turnServers.push({ urls: relay, username: server.username, credential: server.credential });
    }
    this.config.stunUrls = stunUrls;
    this.config.turnServers = turnServers;
    this.config.iceTransportPolicy = policy;
    // A temporary relay failure must be able to fall back to STUN even when a build requested relay-only.
    if (!turnServers.length) this.config.iceTransportPolicyThreshold = undefined;
    for (const [id, peer] of this.peers) {
      void this.trackWork(peer.configureIceServers(servers, this.resolveIcePolicy()).then(offer => {
        if (offer && !this.destroyed && this.peers.get(id) === peer) this.signalingClient.sendOffer(id, { sdp: offer.sdp ?? '' });
      }).catch(() => { /* Keep the healthy transport; future peers use the fresh configuration. */ }));
    }
    this.activeRenditionEngine?.configureIceServers(servers, policy);
    this.preparingRenditionEngine?.configureIceServers(servers, policy);
  }

  /** Identity comes from the backend rendition manifest, never peer messages.
   * Staged memberships isolate pending downloads before the playback facade commits. */
  configureRendition(identity: {id:string;generation:string;protocolVersion:1|2;initSha256:string;codecs:string}): void {
    if (this.privatePublication) throw new Error('Cannot replace private publication with public rendition');
    if(this.joined || this.destroyed) throw new Error('Rendition identity must precede join');
    validateRenditionAuthority(identity);
    this.renditionAuthority = {...identity};
    this.renditionId = identity.id;
    this.invalidatePlaybackCatalog();
  }

  /** A dedicated engine per private medium/generation. Authority comes only from the authenticated control plane. */
  configurePrivatePublication(value: PrivateMediaAuthority): void {
    if (this.joined || this.destroyed || this.renditionAuthority) throw new Error('Private publication must precede configuration and join');
    const authority = parsePrivateMediaAuthority(value);
    if (!authority || authority.keyId !== this.config.keyId || !this.config.transportProfile ||
      Object.entries(authority.transportProfile).some(([key, v]) => v !== this.config.transportProfile![key as keyof typeof authority.transportProfile])) throw new Error('Invalid private publication authority');
    const capabilities = this.config.transportCapabilities;
    if (authority.medium === 'microphone' ? !capabilities?.audioCodecs?.includes('aac') : !capabilities?.videoCodecs.includes('h264')) throw new Error('Unsupported private media capability');
    this.checkPrivateLease(authority);
    this.privatePublication = authority;
    this.renditionAuthority = {id: authority.medium, generation: authority.generation, protocolVersion: 2, initSha256: authority.initSha256, codecs: authority.codecs[0]!};
    this.renditionId = authority.medium;
    this.invalidatePlaybackCatalog();
    this.armPrivateLease();
  }
  getPrivatePublicationAuthority(): PrivateMediaAuthority | null {
    return this.privatePublication ? parsePrivateMediaAuthority(this.privatePublication) : null;
  }
  /** Renewal extends only the existing lease; another incarnation needs a fresh engine/key/cache. */
  renewPrivatePublication(value: PrivateMediaAuthority): void {
    const authority = parsePrivateMediaAuthority(value), current = this.privatePublication;
    if (this.destroyed || !authority || !current || Date.parse(current.expiresAt) <= Date.now() ||
      JSON.stringify({...authority, expiresAt: ''}) !== JSON.stringify({...current, expiresAt: ''})) throw new Error('Foreign or expired private publication renewal');
    this.checkPrivateLease(authority);
    this.privatePublication = authority; this.armPrivateLease();
  }
  private checkPrivateLease(authority: PrivateMediaAuthority): void {
    const remaining = Date.parse(authority.expiresAt) - Date.now();
    if (remaining <= 0 || remaining > 5000) throw new Error('Invalid private publication lease');
  }
  private armPrivateLease(): void {
    if (this.privateLeaseTimer) clearTimeout(this.privateLeaseTimer);
    this.privateLeaseTimer = setTimeout(() => this.destroy(), Math.max(0, Date.parse(this.privatePublication!.expiresAt) - Date.now()));
  }

  /** Rebind a broadcast generation using the backend manifest, retaining the authenticated socket. */
  renewRenditionAuthority(identity:RenditionAuthority):void {
    this.cancelRendition();
    if(this.activeRenditionEngine)return this.activeRenditionEngine.renewRenditionAuthority(identity);
    if(this.destroyed || identity.id!==this.renditionId)throw new Error('Foreign rendition renewal');
    validateRenditionAuthority(identity);
    const w=this.liveWindow;
    const sameTimeline=w?.keyId===identity.generation && w.init?.sha256===identity.initSha256 && w.codecs===identity.codecs;
    this.renditionAuthority={...identity};
    if(sameTimeline)return;
    this.invalidatePlaybackCatalog();
    this.liveWindow=null;
    this.requestLiveWindow();
  }

  join(streamId: string, renditionId = 'main'): Promise<void> {
    return this.trackWork(this.joinOwned(streamId, renditionId));
  }
  private async joinOwned(streamId: string, renditionId: string): Promise<void> {
    this.assertActive();
    if (this.privatePublication && streamId !== this.privatePublication.publicationId) throw new Error('Join differs from private publication');
    if(this.renditionAuthority && renditionId!==this.renditionAuthority.id)throw new Error('Join differs from rendition authority');
    if(this.joined && (streamId!==this.streamId||renditionId!==this.renditionId))throw new Error('Changing swarm requires a fresh engine');
    this.streamId = streamId;
    this.renditionId = renditionId;
    this.signalingClient.connect();

    if (this.joinResolved) {
      this.performJoin();
      return;
    }

    await new Promise<void>((resolve, reject) => {
      const cancelled = () => {
        this.signalingClient.offConnect(onFirstConnect);
        this.joinWaiters.delete(cancelled);
        reject(new Error('P2PEngine destroyed during join'));
      };
      const onFirstConnect = () => {
        this.joinWaiters.delete(cancelled);
        if (this.destroyed) { cancelled(); return; }
        this.signalingClient.offConnect(onFirstConnect);
        this.performJoin();
        this.joinResolved = true;
        resolve();
      };
      this.joinWaiters.add(cancelled);
      if (this.signalingClient.isConnected()) {
        onFirstConnect();
      } else {
        this.signalingClient.onConnect(onFirstConnect);
      }
    });
  }

  private renditionStart(window: LiveWindowPayload, nextSeq: number | null): SegmentAnnounce | undefined {
    if (!window.transportProfile) return nextSeq === null ? window.segments[window.segments.length - 1] : window.segments.find(s => s.seq >= nextSeq);
    return nextSeq === null ? playablePartTail(window.segments)[0]
      : window.segments.find(s => s.seq >= nextSeq && s.independent === true && validPartMetadata(s));
  }

  /** Stage an authenticated membership without disturbing the currently playing catalogue. */
  async prepareRendition(authority:RenditionAuthority, nextSeq:number|null, timeoutMs=8000):Promise<PreparedRendition> {
    // A joined candidate of the same rendition and generation (kept warm, or the rendition playback just left)
    // is reused: no new swarm join, peer id or ICE.
    const existing=this.preparingRenditionEngine;
    const a=existing?.renditionAuthority;
    const reuse=!!existing && !existing.destroyed && existing.joined && !!a && a.id===authority.id &&
      a.generation===authority.generation && a.initSha256===authority.initSha256 && a.codecs===authority.codecs;
    if(reuse){this.renditionAttempt++;this.preparedRendition=null;}else this.cancelRendition();
    if(this.destroyed || this.role!=='viewer')throw new Error('Cannot prepare rendition');
    const attempt=this.renditionAttempt;
    const current=this.activeRenditionEngine ?? this;
    const candidate=reuse?existing!:new P2PEngine({...this.config, peerId:undefined});
    if(!reuse){
      this.preparingRenditionEngine=candidate;
      try {candidate.configureRendition(authority);}catch(err){this.cancelRendition();throw err;}
    }
    candidate.decryption=current.decryption;
    this.watchCandidate(candidate);
    const valid=()=>!this.destroyed && attempt===this.renditionAttempt && candidate.decryption===current.decryption;
    let rejectDeadline:(err:Error)=>void=()=>{};
    const deadline=new Promise<never>((_,reject)=>{rejectDeadline=reject;});
    // Only the latest attempt owns the candidate: a superseded one (the same reused engine) must not tear it down.
    const owns=()=>this.preparingRenditionEngine===candidate && attempt===this.renditionAttempt;
    const timer=setTimeout(()=>{if(owns())this.cancelRendition();rejectDeadline(new Error('Rendition preparation timeout'));},timeoutMs);
    try {
      if(!reuse)await Promise.race([candidate.join(this.streamId,authority.id),deadline]);
      const window=await Promise.race([deadline,new Promise<LiveWindowPayload>((resolve,reject)=>{
        const inspect=()=>{const w=candidate.getLiveWindow();if(w?.init && w.keyId===authority.generation && candidate.renditionStart(w,nextSeq)) {cleanup();resolve(w);}};
        const interval=setInterval(()=>{if(!valid()||candidate.destroyed){cleanup();reject(new Error('Rendition preparation cancelled'));}else inspect();},50);
        const cleanup=()=>clearInterval(interval);
        inspect();
      })]);
      const entry=candidate.renditionStart(window,nextSeq)!;
      const [init,media]=await Promise.all([candidate.requestSegment('init.mp4',authority.initSha256),candidate.requestSegment(entry.segmentId,entry.sha256)]);
      if(!valid() || candidate.destroyed || candidate.getLiveWindow()?.keyId!==authority.generation)throw new Error('Stale rendition preparation');
      const prepared={authority:{...authority},window,entry,init,media};
      this.preparedRendition=prepared;
      return prepared;
    } catch(err) {if(owns())this.cancelRendition();throw err;}
    finally {clearTimeout(timer);}
  }

  validatePreparedRendition(prepared:PreparedRendition):void {
    const candidate=this.preparingRenditionEngine;
    const current=this.activeRenditionEngine ?? this;
    if(this.destroyed || !candidate || prepared!==this.preparedRendition || candidate.destroyed || candidate.decryption!==current.decryption || candidate.getLiveWindow()?.keyId!==prepared.authority.generation)throw new Error('Stale rendition commit');
  }

  /** Keep the authenticated candidate warm when playback passes its first
   * boundary. Only newly verified media replaces the previous preparation. */
  async refreshPreparedRendition(prepared:PreparedRendition,nextSeq:number,timeoutMs=8000):Promise<PreparedRendition> {
    this.validatePreparedRendition(prepared);
    if(!Number.isSafeInteger(nextSeq)||nextSeq<0||!Number.isFinite(timeoutMs)||timeoutMs<=0)throw new Error('Invalid rendition refresh');
    const candidate=this.preparingRenditionEngine!,attempt=this.renditionAttempt;
    let interval:ReturnType<typeof setInterval>|undefined;
    let timer:ReturnType<typeof setTimeout>|undefined;
    const validate=()=>{
      if(attempt!==this.renditionAttempt)throw new Error('Stale rendition refresh');
      this.validatePreparedRendition(prepared);
    };
    const deadline=new Promise<never>((_,reject)=>{timer=setTimeout(()=>reject(new Error('Rendition refresh timeout')),timeoutMs);});
    try {
      const window=await Promise.race([deadline,new Promise<LiveWindowPayload>((resolve,reject)=>{
        const inspect=()=>{
          try {
            validate();const w=candidate.getLiveWindow();
            if(w && candidate.renditionStart(w,nextSeq))resolve(w);
          }catch(err){reject(err);}
        };
        interval=setInterval(inspect,50);inspect();
      })]);
      if(interval!==undefined)clearInterval(interval);
      const entry=candidate.renditionStart(window,nextSeq)!;
      const started=performance.now();
      const media=await Promise.race([deadline,candidate.requestSegment(entry.segmentId,entry.sha256)]);
      validate();
      // A capacity sample is what playback would live through: the whole time from request to verified delivery,
      // including scheduler waits. Pure transfer time looks fine on a high-RTT link whose fragments still arrive late.
      // A fragment the candidate already held (cache hit, no network transfer recorded) is not a sample.
      const timed=candidate.metrics.getTransferMs(entry.segmentId),wall=performance.now()-started;
      const ms=timed>0?Math.max(1,wall):0;
      const samples=ms>0?[...(prepared.samples??[]),{bytes:media.byteLength,ms,durationMs:entry.durationMs}].slice(-6):[...(prepared.samples??[])];
      const refreshed={...prepared,window,entry,media,samples,transferBps:ms>0?media.byteLength*8000/ms:prepared.transferBps};
      this.preparedRendition=refreshed;
      return refreshed;
    }catch(err){
      if(this.preparingRenditionEngine===candidate&&this.preparedRendition===prepared)this.cancelRendition();
      throw err;
    }finally{
      if(interval!==undefined)clearInterval(interval);
      if(timer!==undefined)clearTimeout(timer);
    }
  }

  /** `retainPrevious`: keep the rendition playback leaves joined as the next candidate (a warm way back). */
  commitRendition(prepared:PreparedRendition,opts:{retainPrevious?:boolean}={}):void {
    this.validatePreparedRendition(prepared);
    const candidate=this.preparingRenditionEngine!;
    const old=this.activeRenditionEngine;
    this.preparingRenditionEngine=null;
    this.preparedRendition=null;
    if(old && opts.retainPrevious && !old.destroyed){
      old.forwardOwner=null;
      old.scheduler.cancelAll();
      this.renditionAttempt++;
      this.preparingRenditionEngine=old;
      this.watchCandidate(old);
    }else if(old){old.forwardOwner=null;old.destroy();}else {this.leave();this.signalingClient.destroy();this.scheduler.cancelAll();this.metrics.stop();if(this.peerStatsTimer)clearInterval(this.peerStatsTimer);}
    this.activeRenditionEngine=candidate;
    candidate.forwardOwner=this;
    this.emit('live-window',candidate.getLiveWindow()!);
  }

  private readonly watchedCandidates=new WeakSet<P2PEngine>();
  /** A candidate that ends or loses its authorization is dropped from the slot (once per engine). */
  private watchCandidate(candidate:P2PEngine):void {
    if(this.watchedCandidates.has(candidate))return;
    this.watchedCandidates.add(candidate);
    candidate.on('stream-ended',()=>{if(this.preparingRenditionEngine===candidate)this.cancelRendition();});
    candidate.on('signaling-rejected',info=>{
      if(!info.willRetry && (info.code===4401 || info.code===4410)){
        if(info.code===4401)this.emit('signaling-rejected',info);
        if(this.preparingRenditionEngine===candidate)this.cancelRendition();
      }
    });
  }

  cancelRendition():void {
    this.renditionAttempt++;
    this.preparedRendition=null;
    this.preparingRenditionEngine?.destroy();
    this.preparingRenditionEngine=null;
  }

  getDecryptionKeyId():string|null {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getDecryptionKeyId();
    return this.decryption?.keyId??null;
  }

  getRenditionAuthority():RenditionAuthority|null {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getRenditionAuthority();
    return this.renditionAuthority?{...this.renditionAuthority}:null;
  }

  /** Rendition of the swarm this peer has joined (v1: always 'main'). */
  getRenditionId(): string {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getRenditionId();
    return this.renditionId;
  }

  getRole(): PeerRole {
    return this.role;
  }

  /**
   * Clears cached segments and upload state when the timeline resets
   * (e.g. a backward sequence jump).
   */
  resetSegmentTimeline(reason: string): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.resetSegmentTimeline(reason);
    if (this.destroyed) return;
    this.clearTemporalSegmentStateForJump(-1);
    this.emit('p2p-skip', reason, 'timeline-reset');
  }

  leave(): void {
    this.cancelRendition();
    if(this.activeRenditionEngine)return this.activeRenditionEngine.leave();
    if (!this.joined) return;
    this.joined = false;
    this.signalingClient.sendLeave();
    this.disconnectAllPeers();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const cancel of this.joinWaiters) cancel();
    this.joinWaiters.clear();
    if (this.privateLeaseTimer) clearTimeout(this.privateLeaseTimer);
    this.privateLeaseTimer = null;
    this.catalogEpoch++;
    this.decryption = null;
    this.privatePublication = null;
    this.initData = null; this.initAnnounce = null; this.initCodecs = '';
    this.liveWindow = null; this.recentAnnounces.length = 0;
    this.forwardOwner=null;
    this.cancelRendition();
    if(this.activeRenditionEngine)this.activeRenditionEngine.forwardOwner=null;
    this.activeRenditionEngine?.destroy();
    this.activeRenditionEngine=null;
    // Drop UI listeners first so async peer-disconnected callbacks cannot hit stale hooks.
    for (const key of Object.keys(this.eventHandlers) as (keyof P2PEngineEventMap)[]) {
      delete this.eventHandlers[key];
    }
    if (this.peerStatsTimer) {
      clearInterval(this.peerStatsTimer);
      this.peerStatsTimer = null;
    }
    this.leave();
    this.scheduler.cancelAll();
    this.metrics.stop();
    this.signalingClient.destroy();
    this.disconnectAllPeers();
    this.store.clear();
    for (const timer of this.offerTimers.values()) clearTimeout(timer);
    this.offerTimers.clear();
    this.peers.clear();
    this.peerInfoMap.clear();
    this.peerJoinedAt.clear();
    this.ownSegments.clear();
    this.pendingUploads.clear();
    this.segmentMeta.clear();
    this.uploadFetchTriggered.clear();
    this.seedLiveHeadServed.clear();
    this.seedDeferred.clear();
    this.sentToPeer.clear();
    for (const waiters of this.metaWaiters.values()) {
      for (const w of waiters) {
        clearTimeout(w.timer);
        w.reject(new Error('P2PEngine destroyed'));
      }
    }
    this.metaWaiters.clear();
  }

  /** Abort immediately, then wait for owned origin, publication and playback work to settle. */
  close(): Promise<void> {
    if (this.closePromise) return this.closePromise;
    const active = this.activeRenditionEngine, preparing = this.preparingRenditionEngine;
    const peers = [...this.peers.values()];
    this.destroy();
    this.closePromise = (async () => {
      await Promise.allSettled([active?.close(), preparing?.close(), this.signalingClient.close(), ...peers.map(peer => peer.close())]);
      while (this.pendingWork.size) await Promise.allSettled([...this.pendingWork]);
    })();
    return this.closePromise;
  }

  // ---------------------------------------------------------------------------
  // Viewer API
  // ---------------------------------------------------------------------------

  /**
   * Import the stream AES-128 key. Segments announced with another keyId are rejected.
   * Resolves once the key is ready.
   */
  setDecryptionKey(keyId: string, rawKey: ArrayBuffer | Uint8Array): Promise<void> {
    return this.trackWork(this.setDecryptionKeyOwned(keyId, rawKey));
  }
  private async setDecryptionKeyOwned(keyId: string, rawKey: ArrayBuffer | Uint8Array): Promise<void> {
    this.cancelRendition();
    if(this.activeRenditionEngine)return this.activeRenditionEngine.setDecryptionKey(keyId,rawKey);
    this.assertActive();
    if (this.privatePublication && (keyId !== this.privatePublication.keyId || rawKey.byteLength !== 16)) throw new Error('Invalid private decryption key');
    const key = importAesKey(rawKey, ['decrypt']);
    this.decryption = { keyId, key };
    await key;
  }

  /**
   * Get a segment for playback. Resolves with plaintext fMP4: the encrypted bytes
   * are SHA-256 verified first (over the ciphertext), then AES-GCM decrypted.
   * `init.mp4` is not encrypted and is returned as-is after verification.
   *
   * `sha256` defaults to the catalog (live-window / segment-announce); when the
   * segment has not been announced yet the call waits for its announce.
   */
  requestSegment(segmentId: string, sha256?: string): Promise<ArrayBuffer> {
    return this.trackWork(this.requestSegmentOwned(segmentId, sha256));
  }
  private async requestSegmentOwned(segmentId: string, sha256?: string): Promise<ArrayBuffer> {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.requestSegment(segmentId, sha256);
    this.assertActive();
    const id = canonicalSegmentId(segmentId);
    if (this.role === 'seed') {
      const own = this.getStoredSegmentForUpload(id);
      if (own) return own;
      throw new Error(`Seed does not fetch segments (${id})`);
    }

    const epoch=this.catalogEpoch;
    let hash = sha256?.toLowerCase();
    const expected=this.segmentMeta.get(id)?.sha256;
    if(hash && ((expected && expected!==hash)||(this.renditionAuthority&&!expected)))throw new Error('Hash differs from authorized catalog');
    if (hash) {
      this.rememberSegmentMeta(id, { sha256: hash });
    } else {
      hash = (await this.waitForSegmentMeta(id)).sha256;
    }

    this.observePlaybackSegment(id);
    // Playback pending must exist before upload pull so both paths share one
    // seed fetch (ensureOriginForUpload accelerates the playback pending).
    const playbackPromise = this.scheduler.requestSegment(id, hash);
    this.ensureUploadFetch(id);
    const data = await playbackPromise;
    if(epoch!==this.catalogEpoch)throw new Error('Playback catalog changed during download');

    if (id === INIT_SEGMENT_ID) {
      this.initData = data;
      return data;
    }
    this.recordDeliveryLatency(id);
    const decryption = this.decryption;
    const plaintext = await this.decryptSegment(id, data);
    if (this.destroyed || epoch !== this.catalogEpoch || decryption !== this.decryption) {
      throw new Error('Playback catalog or decryption key changed during decrypt');
    }
    this.assertActive();
    return plaintext;
  }

  /**
   * Register catalog entries (from live-window / segment-announce) so the elected
   * fetcher can serve upload requests and requestSegment knows the hashes.
   */
  registerSegmentCatalog(entries: SegmentCatalogEntry[]): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.registerSegmentCatalog(entries);
    if (this.destroyed) return;
    for (const entry of entries) {
      const id = canonicalSegmentId(entry.segmentId);
      if (!entry.sha256) continue;
      const authority=this.renditionAuthority;
      if(authority){
        if(!/^[a-f0-9]{64}$/.test(entry.sha256))throw new Error('Invalid catalog hash');
        if(id===INIT_SEGMENT_ID&&entry.sha256!==authority.initSha256)throw new Error('Catalog init differs from authority');
        if(id!==INIT_SEGMENT_ID&&entry.keyId!==authority.generation)throw new Error('Catalog generation differs from authority');
      }
      this.rememberSegmentMeta(id, {
        sha256: entry.sha256.toLowerCase(),
        keyId: entry.keyId,
        capturedAt: entry.capturedAt,
      });
      this.ensureUploadFetch(id);
    }
  }

  prefetchSegments(specs: SegmentRequestSpec[]): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.prefetchSegments(specs);
    if (this.role === 'seed') return;
    if(this.renditionAuthority&&specs.some(s=>this.segmentMeta.get(canonicalSegmentId(s.segmentId))?.sha256!==s.sha256.toLowerCase()))throw new Error('Prefetch differs from authorized catalog');
    this.scheduler.prefetchSegments(
      specs.map((s) => ({
        segmentId: canonicalSegmentId(s.segmentId),
        sha256: s.sha256.toLowerCase(),
      })),
    );
  }

  /** Ask the tracker to re-send the live window (answered with a `live-window` event). */
  requestLiveWindow(): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.requestLiveWindow();
    this.signalingClient.send('get-live-window', undefined, { streamId: this.streamId });
  }

  /** Latest live window: tracker snapshot plus segments announced since. */
  getLiveWindow(): LiveWindowPayload | null {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getLiveWindow();
    return this.liveWindow;
  }

  /** Capture timestamp of an announced segment (for glass-to-glass latency). */
  getCapturedAt(segmentId: string): number | undefined {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getCapturedAt(segmentId);
    return this.segmentMeta.get(canonicalSegmentId(segmentId))?.capturedAt;
  }

  // ---------------------------------------------------------------------------
  // Seed API
  // ---------------------------------------------------------------------------

  /** Seed: publish the (unencrypted) fMP4 init segment. */
  publishInit(bytes: ArrayBuffer | Uint8Array, opts: {codecs?: string} = {}): Promise<InitAnnounce> {
    return this.trackWork(this.publishInitOwned(bytes, opts));
  }
  private async publishInitOwned(
    bytes: ArrayBuffer | Uint8Array,
    opts: { codecs?: string } = {},
  ): Promise<InitAnnounce> {
    this.assertSeed('publishInit');
    const data =
      bytes instanceof ArrayBuffer
        ? bytes
        : (bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer);
    const sha256 = await sha256Hex(data);
    this.assertActive();
    if (this.privatePublication && (sha256 !== this.privatePublication.initSha256 || opts.codecs !== this.privatePublication.codecs[0])) throw new Error('Private init differs from authority');
    const id = INIT_SEGMENT_ID;
    if (this.config.transportProfile && this.initAnnounce && this.initAnnounce.sha256 !== sha256) {
      this.invalidatePlaybackCatalog(); this.recentAnnounces.length = 0;
    }
    this.initData = data;
    this.initCodecs = opts.codecs ?? this.initCodecs;
    this.rememberSegmentMeta(id, { sha256 });
    this.store.set(`${segmentCacheKey(this.renditionId, id)}#${sha256}`, data);
    const announce: InitAnnounce = { segmentId: id, sha256, byteLength: data.byteLength };
    this.initAnnounce = announce;
    this.ownSegments.delete(id);
    this.announceSegmentInternal(id);
    this.flushPendingUploads(id);
    this.sendInitAnnounce();
    return announce;
  }

  /**
   * Seed: publish one encrypted media segment. Stores it under the key upload
   * lookups try first, flushes peers waiting for it (subject to the first-hop
   * fan-out cap), announces `have-segment` and sends `segment-announce` to the tracker.
   */
  publishSegment(input: PublishSegmentInput): Promise<SegmentAnnounce> {
    return this.trackWork(this.publishSegmentOwned(input));
  }
  private async publishSegmentOwned(input: PublishSegmentInput): Promise<SegmentAnnounce> {
    this.assertSeed('publishSegment');
    if (this.config.transportProfile && (!validPartMetadata(input) || !Number.isFinite(input.durationMs) || input.durationMs <= 0 || input.durationMs > 2000)) {
      throw new Error('Invalid transport part recovery metadata');
    }
    const sha256 = (input.sha256 ?? (await sha256Hex(input.ciphertext))).toLowerCase();
    this.assertActive();
    const keyId = input.keyId ?? this.config.keyId ?? '';
    const id = segmentIdForSeq(input.seq);

    // New live head: the previous one is no longer fan-out capped, so the
    // requests deferred over the cap are served now.
    const deferred = [...this.seedDeferred];
    this.seedDeferred.clear();
    this.seedLiveHeadServed.clear();
    this.observePlaybackSegment(id);
    this.rememberSegmentMeta(id, { sha256, keyId, capturedAt: input.capturedAt });
    this.store.set(`${segmentCacheKey(this.renditionId, id)}#${sha256}`, input.ciphertext);
    this.segmentsPublished++;
    for (const [deferredId, peers] of deferred) {
      const data = this.getStoredSegmentForUpload(deferredId);
      if (!data) continue;
      for (const peerId of peers) this.serveUpload(deferredId, peerId, data);
    }

    const announce: SegmentAnnounce = {
      segmentId: id,
      seq: input.seq,
      durationMs: input.durationMs,
      sha256,
      byteLength: input.ciphertext.byteLength,
      capturedAt: input.capturedAt,
      keyId,
      ...(this.config.transportProfile ? {independent: input.independent, gopStartSeq: input.gopStartSeq} : {}),
    };
    this.recentAnnounces.push(announce);
    if (this.config.transportProfile) this.trimLiveWindow(this.recentAnnounces);
    else if (this.recentAnnounces.length > SEED_RECENT_ANNOUNCES) this.recentAnnounces.shift();

    this.announceSegmentInternal(id);
    this.flushPendingUploads(id);
    const payload: SegmentAnnouncePayload = { segment: announce };
    this.signalingClient.send('segment-announce', undefined, payload);
    return announce;
  }

  /** Seed: tell the tracker the broadcast ended (viewers get `stream-ended`). */
  endStream(reason?: string): void {
    this.assertSeed('endStream');
    const payload: StreamEndedPayload = { streamId: this.streamId, reason };
    this.signalingClient.send('stream-ended', undefined, payload);
  }

  /**
   * Seed: open or close this seed to viewers' connections (the privacy fallback of a hidden broadcast). Open: it answers
   * offers again and serves `fanout` first-hop viewers, so its address becomes reachable. Closed: it answers nobody
   * and every connection it holds to a viewer is dropped at once.
   */
  setViewerAccess(open: boolean, fanout: number): void {
    this.assertSeed('setViewerAccess');
    const config = this.config as { acceptPeerConnections: boolean; seedFirstHopFanout: number };
    config.acceptPeerConnections = open;
    config.seedFirstHopFanout = fanout;
    if (!open) this.disconnectAllPeers();
  }

  /** Seed of a stream that joined hidden: tell the tracker (so its viewers) that the seed is exposed or hidden again. */
  announceExposure(exposed: boolean, reason?: string): void {
    this.assertSeed('announceExposure');
    const payload: SeedExposurePayload = { exposed, ...(reason ? { reason } : {}) };
    this.signalingClient.send('seed-exposure', undefined, payload);
  }

  // ---------------------------------------------------------------------------
  // Shared API
  // ---------------------------------------------------------------------------

  announceSegment(segmentId: string): void {
    this.announceSegmentInternal(canonicalSegmentId(segmentId));
  }

  recordRebuffer(durationMs: number): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.recordRebuffer(durationMs);
    this.metrics.recordRebuffer(durationMs);
  }

  recordWatched(durationMs: number): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.recordWatched(durationMs);
    this.metrics.recordWatched(durationMs);
  }

  setLatencyToLiveEdgeMs(ms: number): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.setLatencyToLiveEdgeMs(ms);
    this.playerReportsLatency = true;
    this.metrics.setLatencyToLiveEdgeMs(ms);
    this.scheduler.setPlaybackState({ latencyToLiveEdgeMs: ms });
  }

  setPlaybackState(state: PlaybackState): void {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.setPlaybackState(state);
    if (typeof state.latencyToLiveEdgeMs === 'number') {
      this.playerReportsLatency = true;
      this.metrics.setLatencyToLiveEdgeMs(state.latencyToLiveEdgeMs);
    }
    this.scheduler.setPlaybackState(state);
  }

  on<K extends keyof P2PEngineEventMap>(
    event: K,
    cb: P2PEngineEventMap[K],
  ): void {
    const list = (this.eventHandlers[event] ?? []) as Array<
      P2PEngineEventMap[K]
    >;
    list.push(cb);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this.eventHandlers as any)[event] = list;
  }

  off<K extends keyof P2PEngineEventMap>(
    event: K,
    cb: P2PEngineEventMap[K],
  ): void {
    const list = this.eventHandlers[event] as Array<P2PEngineEventMap[K]> | undefined;
    if (!list) return;
    const idx = list.indexOf(cb);
    if (idx !== -1) list.splice(idx, 1);
  }

  getMetrics(): P2PEngineMetrics {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getMetrics();
    return {
      ...this.metrics.snapshot(),
      ...this.metrics.getDeliveryTotals(),
      network: this.metrics.getNetworkTotals(),
      receiveThroughputBps: this.metrics.getReceiveThroughputBps(),
      seedConnected: this.getConnectedSeedIds().length > 0,
      peerId: this.peerId,
      role: this.role,
      segmentsPublished: this.segmentsPublished,
      bytesUploaded: this.bytesUploaded + [...this.peers.values()].reduce((n, conn) => n + (conn.uploadSnapshot?.bytesSent ?? 0), 0),
      confirmedUploadBytes: this.confirmedUploadBytes + [...this.peers.values()].reduce((n, conn) => n + (conn.uploadSnapshot?.confirmedBytes ?? 0), 0),
      confirmedUploadDeliveries: this.confirmedUploadDeliveries + [...this.peers.values()].reduce((n, conn) => n + (conn.uploadSnapshot?.confirmedDeliveries ?? 0), 0),
      queuedUploadBytes: [...this.peers.values()].reduce((n, conn) => n + (conn.uploadSnapshot?.queuedBytes ?? 0), 0),
    };
  }

  /** Scheduler role for diagnostics; an unassigned v2 viewer has no established role yet. */
  getMediaElectionRole(): 'solo' | 'fetcher' | 'relay' | null {
    if (this.activeRenditionEngine) return this.activeRenditionEngine.getMediaElectionRole();
    if (this.config.transportProfile) {
      if (!this.deliveryAssignment) return null;
      return this.deliveryAssignment.originFetcher ? 'fetcher' : 'relay';
    }
    if (this.viewerPeerIds().length === 0) return 'solo';
    const probe = segmentIdForSeq(Math.max(0, this.highestSegmentSequence));
    return this.electedFetcherId(probe) === this.peerId ? 'fetcher' : 'relay';
  }

  getPeerCount(): number {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getPeerCount();
    let count = 0;
    for (const conn of this.peers.values()) {
      if (conn.isOpen()) count++;
    }
    return count;
  }

  getConnectedPeerIds(): string[] {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getConnectedPeerIds();
    const ids: string[] = [];
    for (const [peerId, conn] of this.peers) {
      if (conn.isOpen()) ids.push(peerId);
    }
    return ids;
  }

  /** Viewers in the swarm per the tracker's last peer-list (excludes self and seeds). */
  getSwarmSize(): number {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getSwarmSize();
    return this.swarmPeerCount;
  }

  getTopPeers(): ReturnType<MetricsCollector['getTopPeers']> {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getTopPeers();
    return this.metrics.getTopPeers();
  }

  getPeerId(): string {
    if(this.activeRenditionEngine)return this.activeRenditionEngine.getPeerId();
    return this.peerId;
  }

  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------

  private assertActive(): void {
    if (this.privatePublication && Date.parse(this.privatePublication.expiresAt) <= Date.now()) {
      this.destroy(); throw new Error('Private publication expired');
    }
    if (this.destroyed) throw new Error('P2PEngine is destroyed');
  }
  private assertSeed(method: string): void {
    if (this.role !== 'seed') throw new Error(`${method}() is only available in seed role`);
    this.assertActive();
  }

  private isSeed(peerId: string): boolean {
    return this.seedPeerIds.has(peerId) || isSeedPeerId(peerId);
  }

  /** Non-seed peers with a PeerConnection (connecting or open). */
  private viewerPeerIds(): string[] {
    return [...this.peers.keys()].filter((pid) => !this.isSeed(pid));
  }

  private getConnectedSeedIds(): string[] {
    const ids: string[] = [];
    for (const [peerId, conn] of this.peers) {
      if (this.isSeed(peerId) && conn.isOpen()) ids.push(peerId);
    }
    return ids;
  }

  private async decryptSegment(segmentId: string, data: ArrayBuffer): Promise<ArrayBuffer> {
    const dec = this.decryption;
    if (!dec) throw new Error(`No decryption key set for ${segmentId}`);
    const keyId = this.segmentMeta.get(segmentId)?.keyId;
    if (keyId && keyId !== dec.keyId) {
      throw new Error(`Segment ${segmentId} is encrypted with key ${keyId}, engine has ${dec.keyId}`);
    }
    const authority=this.renditionAuthority;
    const aad=authority?.protocolVersion===2?`${keyId ?? dec.keyId}:${authority.id}:${segmentId}`:segmentAad(keyId ?? dec.keyId,segmentId);
    return aesGcmDecrypt(await dec.key,data,aad);
  }

  private invalidatePlaybackCatalog(): void {
    this.catalogEpoch++;
    this.sentToPeer.clear();
    for (const peer of this.peers.values()) peer.invalidateQueuedSends?.();
    this.pendingLegacyInit = null;
    this.scheduler.cancelAll();
    this.segmentMeta.clear();
    this.store.clear();
    this.ownSegments.clear();
    for (const peer of this.peerInfoMap.values()) {
      peer.availableSegments.clear(); this.scheduler.updatePeer(peer);
    }
    this.initData=null;
    this.uploadFetchTriggered.clear();
    for(const waiters of this.metaWaiters.values())for(const w of waiters){clearTimeout(w.timer);w.reject(new Error('Playback catalog changed'));}
    this.metaWaiters.clear();
  }

  private recordDeliveryLatency(segmentId: string): void {
    if (this.playerReportsLatency) return;
    const capturedAt = this.segmentMeta.get(segmentId)?.capturedAt;
    if (typeof capturedAt === 'number') {
      this.metrics.setLatencyToLiveEdgeMs(Math.max(0, Date.now() - capturedAt));
    }
  }

  private rememberSegmentMeta(segmentId: string, meta: SegmentMeta): void {
    const id = canonicalSegmentId(segmentId);
    if (!meta.sha256) return;
    const prev = this.segmentMeta.get(id);
    const merged: SegmentMeta = {
      sha256: meta.sha256,
      keyId: meta.keyId ?? prev?.keyId,
      capturedAt: meta.capturedAt ?? prev?.capturedAt,
    };
    this.segmentMeta.delete(id);
    this.segmentMeta.set(id, merged);
    while (this.segmentMeta.size > MAX_SEGMENT_META) {
      const oldest = [...this.segmentMeta.keys()].find((k) => k !== INIT_SEGMENT_ID);
      if (oldest === undefined) break;
      this.segmentMeta.delete(oldest);
    }
    const waiters = this.metaWaiters.get(id);
    if (waiters) {
      this.metaWaiters.delete(id);
      for (const w of waiters) {
        clearTimeout(w.timer);
        w.resolve(merged);
      }
    }
  }

  private waitForSegmentMeta(segmentId: string): Promise<SegmentMeta> {
    const existing = this.segmentMeta.get(segmentId);
    if (existing) return Promise.resolve(existing);
    const timeoutMs = Math.max(5_000, this.config.segmentDurationMs * 5);
    return new Promise<SegmentMeta>((resolve, reject) => {
      const waiter: MetaWaiter = {
        resolve,
        reject,
        timer: setTimeout(() => {
          const list = this.metaWaiters.get(segmentId);
          if (list) {
            const idx = list.indexOf(waiter);
            if (idx !== -1) list.splice(idx, 1);
            if (list.length === 0) this.metaWaiters.delete(segmentId);
          }
          reject(new Error(`Segment ${segmentId} was not announced within ${timeoutMs}ms`));
        }, timeoutMs),
      };
      const list = this.metaWaiters.get(segmentId) ?? [];
      list.push(waiter);
      this.metaWaiters.set(segmentId, list);
    });
  }

  private performJoin(): void {
    this.joined = true;
    this.scheduler.setRendition(this.renditionId);
    this.deliveryAssignment = null;
    this.signalingClient.sendJoin({
      peerId: this.peerId,
      streamId: this.streamId,
      renditionId: this.renditionId,
      region: this.config.region,
      role: this.role,
      ...(this.config.transportProfile ? {generation: this.config.keyId, capabilities: this.config.transportCapabilities} : {}),
    });
    this.resyncOwnSegments();
    if (this.role === 'seed') {
      // Re-seed the tracker's live window after (re)connecting. Trackers must
      // de-duplicate announces by segmentId.
      this.sendInitAnnounce();
      for (const segment of this.recentAnnounces) {
        if (this.isStaleSegment(segment.segmentId)) continue;
        const payload: SegmentAnnouncePayload = { segment };
        this.signalingClient.send('segment-announce', undefined, payload);
      }
    }
  }

  private sendInitAnnounce(): void {
    if (!this.initAnnounce) return;
    const payload: SegmentAnnouncePayload = {
      init: this.initAnnounce,
      codecs: this.initCodecs,
      segmentDurationMs: this.config.segmentDurationMs,
    };
    this.signalingClient.send('segment-announce', undefined, payload);
  }

  /**
   * Re-sync this peer's holdings with the tracker (after a join or a peer list) with one
   * `segment-map`. It used to add one `have-segment` per held segment as well: about 31 messages
   * per peer list, so a reconnecting viewer exceeded the tracker's per-socket rate limit (burst 50)
   * and the tracker dropped what followed, including its offer and ICE candidates to the seed.
   * New holdings are still announced one by one as they arrive (announceSegmentInternal).
   */
  private resyncOwnSegments(): void {
    this.pruneTemporalSegmentState();
    if (this.ownSegments.size === 0) return;
    this.signalingClient.sendSegmentMap({
      peerId: this.peerId,
      segments: Array.from(this.ownSegments),
      ...(this.config.transportProfile ? {generation: this.config.keyId} : {}),
    });
  }

  private announceSegmentInternal(segmentId: string): void {
    const id = canonicalSegmentId(segmentId);
    if (this.isStaleSegment(id)) return;
    if (this.ownSegments.has(id)) return;
    this.ownSegments.add(id);
    this.pruneTemporalSegmentState();
    this.signalingClient.sendHaveSegment({
      segmentId: id,
      renditionId: this.renditionId,
      ...(this.config.transportProfile ? {generation: this.config.keyId} : {}),
    });
  }

  private observePlaybackSegment(segmentId: string): void {
    const seq = segmentSequence(segmentId);
    if (seq === null) return;

    if (
      this.highestSegmentSequence >= 0 &&
      seq > this.highestSegmentSequence + SEGMENT_JUMP_RESET_THRESHOLD
    ) {
      this.clearTemporalSegmentStateForJump(seq);
    }

    if (
      this.highestSegmentSequence >= 0 &&
      seq < this.highestSegmentSequence - SEGMENT_JUMP_RESET_THRESHOLD
    ) {
      this.clearTemporalSegmentStateForJump(seq);
    }

    if (seq > this.highestSegmentSequence) {
      this.highestSegmentSequence = seq;
      this.pruneTemporalSegmentState();
    }
  }

  private retainWindow(): number {
    const durationCount = this.config.transportProfile ? Math.ceil(18_000 / this.config.transportProfile.partDurationMs) : LIVE_SEGMENT_RETAIN_WINDOW;
    return Math.min(durationCount, this.config.retainSegments);
  }

  private trimLiveWindow(entries: SegmentAnnounce[]): void {
    if (this.config.transportProfile) trimMediaWindow(entries, {maxDurationMs: 30_000, maxBytes: 64 * 1024 * 1024,
      maxEntries: Math.ceil(30_000 / this.config.transportProfile.partDurationMs)});
    else while (entries.length > LIVE_WINDOW_MAX_SEGMENTS) entries.shift();
  }

  private minRetainedSegmentSequence(): number {
    if (this.highestSegmentSequence < 0) return Number.NEGATIVE_INFINITY;
    return this.highestSegmentSequence - this.retainWindow();
  }

  private isStaleSegment(segmentId: string): boolean {
    const seq = segmentSequence(segmentId);
    return seq !== null && seq < this.minRetainedSegmentSequence();
  }

  private clearTemporalSegmentStateForJump(currentSeq: number): void {
    this.pendingUploads.clear();
    this.uploadFetchTriggered.clear();
    this.scheduler.cancelAll();
    this.store.clear();
    // Catalog metadata is kept (the announced window is still valid after a
    // live-edge seek); stale entries are pruned below.
    this.highestSegmentSequence = currentSeq >= 0 ? currentSeq : -1;
    this.pruneTemporalSegmentState();
    if (currentSeq >= 0) {
      this.emit('p2p-skip', 'segment sequence jump', segmentIdForSeq(currentSeq));
    }
  }

  private pruneTemporalSegmentState(): void {
    const minSeq = this.minRetainedSegmentSequence();
    if (!Number.isFinite(minSeq)) return;

    for (const segmentId of Array.from(this.ownSegments)) {
      const seq = segmentSequence(segmentId);
      if (seq !== null && seq < minSeq) {
        this.ownSegments.delete(segmentId);
      }
    }

    for (const segmentId of Array.from(this.pendingUploads.keys())) {
      const seq = segmentSequence(segmentId);
      if (seq !== null && seq < minSeq) {
        this.pendingUploads.delete(segmentId);
        this.uploadFetchTriggered.delete(segmentId);
      }
    }

    for (const segmentId of Array.from(this.segmentMeta.keys())) {
      const seq = segmentSequence(segmentId);
      if (seq !== null && seq < minSeq) {
        this.segmentMeta.delete(segmentId);
      }
    }

    for (const sent of this.sentToPeer.values()) {
      for (const segmentId of sent) {
        const seq = segmentSequence(segmentId);
        if (seq !== null && seq < minSeq) sent.delete(segmentId);
      }
    }
  }

  private startPeerStatsReporting(): void {
    this.peerStatsTimer = setInterval(() => {
      if (!this.joined || this.destroyed) return;

      let queuedBytes = 0;
      this.localUploadRate = 0;
      for (const conn of this.peers.values()) {
        const sample = conn.uploadSnapshot;
        this.localUploadRate += sample?.bytesPerSecond ?? 0;
        queuedBytes += sample?.queuedBytes ?? 0;
      }

      const snap = this.metrics.snapshot();
      if (snap.p2pOffloadRatio > 0 || snap.originFallbackRate > 0) {
        this.localReliability = Math.max(
          0.1,
          snap.p2pOffloadRatio * 0.7 + (1 - snap.originFallbackRate) * 0.3,
        );
      }

      const capacity = this.uploadCapacity.snapshot();
      const payload: PeerStatsPayload = {
        ...(this.config.transportProfile ? {generation: this.config.keyId,
          confirmedUploadRate: capacity.confirmedRate,
          confirmedUploadDeliveries: capacity.confirmedDeliveries,
          uploadDeadlineSuccess: capacity.deadlineSuccess} : {}),
        reliability: this.localReliability,
        uploadRate: this.localUploadRate,
        queuedBytes,
        rttMs: this.averageLocalRtt(),
      };

      this.signalingClient.sendPeerStats(payload);
    }, this.config.transportProfile ? 1000 : PEER_STATS_INTERVAL_MS);
  }

  private averageLocalRtt(): number {
    const rtts = Array.from(this.peers.values()).map((p) => p.measuredRttMs);
    if (rtts.length === 0) return 100;
    return Math.round(rtts.reduce((a, b) => a + b, 0) / rtts.length);
  }

  private registerSignalingHandlers(): void {
    this.signalingClient.onConnect(() => {
      if (this.joinResolved && this.streamId && !this.destroyed) {
        this.performJoin();
        this.emit('signaling-reconnected');
      }
    });

    this.signalingClient.on('peer-list', (msg: SignalingMessage) => {
      const payload = msg.payload as PeerListPayload;
      if (!payload || !Array.isArray(payload.peers)) return;
      if (this.config.transportProfile && this.role === 'viewer') {
        const a = payload.assignment;
        const identical = this.deliveryAssignment && JSON.stringify(a) === JSON.stringify(this.deliveryAssignment);
        if (!identical && !acceptsDeliveryAssignment(a, this.config.keyId!, this.peerId, this.deliveryAssignment?.revision ?? 0)) return;
        this.deliveryAssignment = {...a!, neighbors: [...a!.neighbors]};
        this.scheduler.refreshDeliveryAssignment();
      }
      if (typeof payload.selfJoinedAt === 'number') this.selfJoinedAt = payload.selfJoinedAt;
      for (const p of payload.peers) {
        if (p.role === 'seed') this.seedPeerIds.add(p.peerId);
        if (typeof p.joinedAt === 'number') this.peerJoinedAt.set(p.peerId, p.joinedAt);
      }
      this.swarmPeerCount = payload.peers.filter(
        (p) => p.peerId !== this.peerId && !this.isSeed(p.peerId),
      ).length;
      const activePeerIds = new Set(
        payload.peers
          .filter((p) => p.peerId !== this.peerId)
          .map((p) => p.peerId),
      );
      if (this.config.transportProfile && this.role === 'viewer') {
        for (const id of activePeerIds) if (!this.isSeed(id) && !this.deliveryAssignment?.neighbors.includes(id)) activePeerIds.delete(id);
        for (const id of [...this.peers.keys()]) if (!activePeerIds.has(id) && !this.isSeed(id)) this.removePeer(id);
      }
      this.listedPeerIds = activePeerIds;
      for (const peerId of Array.from(this.peerInfoMap.keys())) {
        if (!activePeerIds.has(peerId)) {
          this.peerInfoMap.delete(peerId);
          this.peerJoinedAt.delete(peerId);
          this.scheduler.removePeer(peerId);
          this.clearPendingUploadsForPeer(peerId);
          if (this.peers.has(peerId)) this.removePeer(peerId);
        }
      }
      // Tras peer-list, re-sincronizar segmentos propios.
      this.resyncOwnSegments();

      for (const p of payload.peers) {
        if (p.peerId === this.peerId || !activePeerIds.has(p.peerId)) continue;
        const seed = this.isSeed(p.peerId);
        // Seeds stay out of the scheduler's peer table: they are reached only
        // through the origin path, so a seed `have-segment` never triggers a
        // reactive request that would bypass the relay window.
        if (!seed) {
          const info: PeerInfo = {
            peerId: p.peerId,
            availableSegments: new Set(
              p.availableSegments
                .map((s) => canonicalSegmentId(s))
                .filter((s) => !this.isStaleSegment(s)),
            ),
            uploadRate: p.uploadRate,
            reliability: p.reliability,
            rttMs: 100,
            region: p.region,
          };
          this.peerInfoMap.set(p.peerId, info);
          this.scheduler.updatePeer(info);
        }

        if (
          !this.peers.has(p.peerId) &&
          (this.config.transportProfile ? this.canConnect(p.peerId) : seed || this.peers.size < this.config.maxPeers) &&
          this.shouldInitiateConnection(p.peerId)
        ) {
          void this.initiateConnection(p.peerId);
        }
      }
    });

    this.signalingClient.on('offer', (msg: SignalingMessage) => {
      void this.handleOffer(msg.from, msg.payload as OfferPayload);
    });

    this.signalingClient.on('answer', (msg: SignalingMessage) => {
      void this.handleAnswer(msg.from, msg.payload as AnswerPayload);
    });

    this.signalingClient.on('ice-candidate', (msg: SignalingMessage) => {
      void this.handleIceCandidate(
        msg.from,
        msg.payload as IceCandidatePayload,
      );
    });

    this.signalingClient.on('segment-map', (msg: SignalingMessage) => {
      const payload = msg.payload as SegmentMapPayload;
      if (this.config.transportProfile && payload.generation !== this.config.keyId) return;
      const info = this.peerInfoMap.get(payload.peerId);
      if (info) {
        info.availableSegments = new Set(
          payload.segments
            .map((s) => canonicalSegmentId(s))
            .filter((s) => !this.isStaleSegment(s)),
        );
        this.scheduler.updatePeer(info);
      }
    });

    this.signalingClient.on('peer-stats', (msg: SignalingMessage) => {
      const payload = msg.payload as PeerStatsPayload;
      if (this.config.transportProfile && payload.generation !== this.config.keyId) return;
      const info = this.peerInfoMap.get(msg.from);
      if (info) {
        info.reliability = payload.reliability;
        info.uploadRate = payload.uploadRate;
        // Prefer locally measured RTT (real WebRTC round-trip) over self-reported
        const directConn = this.peers.get(msg.from);
        info.rttMs = directConn ? directConn.measuredRttMs : payload.rttMs;
        this.scheduler.updatePeer(info);
      }
    });

    this.signalingClient.on('have-segment', (msg: SignalingMessage) => {
      if (this.isSeed(msg.from)) return;
      const payload = msg.payload as { segmentId: string; renditionId?: string; generation?: string };
      if (this.config.transportProfile && payload.generation !== this.config.keyId) return;
      if (payload.renditionId && payload.renditionId !== this.renditionId) return;
      const segmentId = canonicalSegmentId(payload.segmentId);
      if (this.isStaleSegment(segmentId)) return;
      const info = this.peerInfoMap.get(msg.from);
      if (info) {
        if (info.availableSegments.has(segmentId)) return;
        info.availableSegments.add(segmentId);
        this.scheduler.updatePeer(info, segmentId);
      }
    });

    // Holders of one segment (tracker answer to request-segment). Unlike
    // peer-list this is not the whole swarm: merge, never prune.
    this.signalingClient.on('segment-holders', (msg: SignalingMessage) => {
      if (this.role === 'seed') return;
      const payload = msg.payload as Partial<SegmentHoldersPayload> | null;
      if (!payload || typeof payload.segmentId !== 'string' || !Array.isArray(payload.peers)) return;
      if (this.config.transportProfile && payload.generation !== this.config.keyId) return;
      const id = canonicalSegmentId(payload.segmentId);
      if (!SEGMENT_ID_REGEX.test(id) || this.isStaleSegment(id)) return;
      for (const h of payload.peers) {
        if (!h || typeof h.peerId !== 'string') continue;
        if (h.role === 'seed') this.seedPeerIds.add(h.peerId);
        if (h.peerId === this.peerId || this.isSeed(h.peerId)) continue;
        if (this.config.transportProfile && !this.deliveryAssignment?.neighbors.includes(h.peerId)) continue;
        if (typeof h.joinedAt === 'number') this.peerJoinedAt.set(h.peerId, h.joinedAt);
        let info = this.peerInfoMap.get(h.peerId);
        if (!info) {
          info = {
            peerId: h.peerId,
            availableSegments: new Set(),
            uploadRate: h.uploadRate ?? 0,
            reliability: h.reliability ?? 0.5,
            rttMs: 100,
            region: h.region,
          };
          this.peerInfoMap.set(h.peerId, info);
        }
        if (info.availableSegments.has(id)) continue;
        info.availableSegments.add(id);
        this.scheduler.updatePeer(info, id);
      }
    });

    this.signalingClient.on('live-window', (msg: SignalingMessage) => {
      if (this.role === 'seed') return;
      const window = msg.payload as LiveWindowPayload;
      if(this.streamId&&window.streamId!==this.streamId)return;
      if (this.config.transportProfile && (window.generation !== this.config.keyId ||
          JSON.stringify(window.transportProfile) !== JSON.stringify(this.config.transportProfile))) return;
      if (this.config.transportProfile && (!Array.isArray(window.segments) || window.segments.some(p => !validPartMetadata(p) || p.keyId !== this.config.keyId))) return;
      const authority=this.renditionAuthority;
      if(authority && (window.renditionId!==authority.id||window.generation!==authority.generation||window.protocolVersion!==authority.protocolVersion||window.codecs!==authority.codecs||window.init?.sha256!==authority.initSha256)){
        this.invalidatePlaybackCatalog();
        if(window.keyId&&window.keyId!==authority.generation)this.emit('key-changed',{keyId:window.keyId,previousKeyId:authority.generation});
        return;
      }
      if (window.seedPeerId) this.seedPeerIds.add(window.seedPeerId);
      this.mediaOrigin = window.mediaOrigin === 'proxy' ? 'proxy' : 'seed';
      if (this.config.transportProfile && this.mediaOrigin === 'proxy') {
        for (const id of this.seedPeerIds) this.removePeer(id);
      }
      this.pendingLegacyInit = null;
      const previousKeyId = this.announcedKeyId;
      if ((previousKeyId && window.keyId && previousKeyId !== window.keyId) ||
          (this.config.transportProfile && this.liveWindow?.init && this.liveWindow.init.sha256 !== window.init?.sha256)) this.invalidatePlaybackCatalog();
      if (window.keyId) this.announcedKeyId = window.keyId;
      this.liveWindow = {
        ...window,
        segments: [...window.segments],
      };
      this.trimLiveWindow(this.liveWindow.segments);
      const entries: SegmentCatalogEntry[] = [...this.liveWindow.segments];
      if (window.init) entries.push(window.init);
      this.registerSegmentCatalog(entries);
      this.emit('live-window', this.liveWindow);
      if (previousKeyId && window.keyId && window.keyId !== previousKeyId) {
        this.emit('key-changed', { keyId: window.keyId, previousKeyId });
      }
    });

    this.signalingClient.on('segment-announce', (msg: SignalingMessage) => {
      if (this.role === 'seed') return;
      const raw = msg.payload as SegmentAnnouncePayload | SegmentAnnounce;
      const payload: SegmentAnnouncePayload =
        'segmentId' in raw ? { segment: raw } : raw;
      if (this.config.transportProfile && payload.segment && payload.segment.keyId !== this.config.keyId) return;
      if (this.config.transportProfile && payload.segment && !validPartMetadata(payload.segment)) return;
      const authority=this.renditionAuthority;
      if(authority&&((payload.init&&payload.init.sha256!==authority.initSha256)||(payload.codecs!==undefined&&payload.codecs!==authority.codecs)||(payload.segment&&payload.segment.keyId!==authority.generation)))return;
      // Rotate before registering a combined init+segment announcement. A separate
      // changed init announcement may also belong to this replacement generation.
      const segment = payload.segment;
      if (segment?.keyId && segment.keyId !== this.announcedKeyId) {
        const previousKeyId = this.announcedKeyId;
        const replacementInit = payload.init ?? this.pendingLegacyInit;
        if (previousKeyId) this.invalidatePlaybackCatalog();
        this.announcedKeyId = segment.keyId;
        if (this.liveWindow) {
          this.liveWindow.segments = [];
          this.liveWindow.keyId = segment.keyId;
          if (previousKeyId) this.liveWindow.init = replacementInit ?? null;
        }
        if (previousKeyId) {
          if (replacementInit) this.registerSegmentCatalog([replacementInit]);
          else this.requestLiveWindow();
          this.emit('key-changed', { keyId: segment.keyId, previousKeyId });
        }
      }
      let createdWindow = false;
      if (payload.init) {
        if (this.config.transportProfile && this.liveWindow?.init && this.liveWindow.init.sha256 !== payload.init.sha256) {
          this.invalidatePlaybackCatalog(); this.liveWindow.segments = [];
        }
        if (!authority && !segment && this.announcedKeyId && payload.init.sha256 !== this.liveWindow?.init?.sha256) {
          this.pendingLegacyInit = payload.init;
        }
        this.registerSegmentCatalog([payload.init]);
        if (this.liveWindow) {
          this.liveWindow.init = payload.init;
          if (payload.codecs) this.liveWindow.codecs = payload.codecs;
          if (payload.segmentDurationMs) this.liveWindow.segmentDurationMs = payload.segmentDurationMs;
        } else {
          // Joined before the seed published init: no live-window was sent, so
          // build one from this announce and let the player start.
          const seedPeerId = msg.from && msg.from !== 'server' ? msg.from : '';
          if (seedPeerId) this.seedPeerIds.add(seedPeerId);
          this.liveWindow = {
            streamId: this.streamId,
            codecs: payload.codecs ?? '',
            segmentDurationMs: payload.segmentDurationMs ?? this.config.segmentDurationMs,
            keyId: this.announcedKeyId,
            seedPeerId,
            init: payload.init,
            segments: [],
          };
          createdWindow = true;
        }
      }
      if (createdWindow) this.emit('live-window', this.liveWindow!);
      if (segment) {
        this.registerSegmentCatalog([segment]);
        if (this.liveWindow && !this.liveWindow.segments.some((s) => s.segmentId === segment.segmentId)) {
          this.liveWindow.segments.push(segment);
          this.trimLiveWindow(this.liveWindow.segments);
        }
        this.emit('segment-announce', segment);
      }
    });

    this.signalingClient.on('stream-ended', (msg: SignalingMessage) => {
      const payload = (msg.payload ?? {}) as Partial<StreamEndedPayload>;
      this.emit('stream-ended', {
        streamId: payload.streamId ?? this.streamId,
        reason: payload.reason,
      });
    });
  }

  /**
   * A peer asked us for a segment over the DataChannel. Ids that are not valid
   * segment ids are ignored; requests we cannot serve now are NACKed so the
   * requester tries another source at once.
   */
  private handleSegmentUploadRequest(
    segmentId: string,
    fromPeerId: string,
  ): void {
    const id = canonicalSegmentId(segmentId);
    if (!SEGMENT_ID_REGEX.test(id)) return;
    const nack = () => {
      this.peers.get(fromPeerId)?.sendNack(id);
    };
    if (this.isStaleSegment(id)) return nack();
    const data = this.getStoredSegmentForUpload(id);
    if (data) {
      this.serveUpload(id, fromPeerId, data);
      return;
    }
    // The seed is the origin: what it lacks does not exist yet. Never queue it.
    if (this.role === 'seed') return nack();
    // Unknown to the catalog, or too many segments already waiting: decline.
    if (!this.segmentMeta.has(id)) return nack();
    if (!this.pendingUploads.has(id) && this.pendingUploads.size >= this.config.maxPendingUploads) {
      return nack();
    }
    this.queuePendingUpload(id, fromPeerId);
    this.ensureUploadFetch(id);
  }

  /**
   * Upload one segment to one peer, applying the per-peer limits (no duplicate,
   * max in-flight) and, on the seed, the live-head fan-out cap (over the cap the
   * request is deferred to the next publish and NACKed now).
   */
  private serveUpload(id: string, peerId: string, data: ArrayBuffer): UploadOutcome {
    const conn = this.peers.get(peerId);
    if (!conn) return 'unavailable';
    if (this.sentToPeer.get(peerId)?.has(id)) return 'duplicate';
    if (conn.pendingSendCount() >= this.config.maxInflightUploadsPerPeer) {
      conn.sendNack(id);
      return 'nacked';
    }
    if (!this.admitSeedUpload(id, peerId)) {
      let peers = this.seedDeferred.get(id);
      if (!peers) {
        peers = new Set();
        this.seedDeferred.set(id, peers);
      }
      peers.add(peerId);
      conn.sendNack(id);
      return 'deferred';
    }
    if (!conn.isOpen() || !conn.sendSegment(id, data, true, this.segmentMeta.get(id)?.sha256)) return 'unavailable';
    let sent = this.sentToPeer.get(peerId);
    if (!sent) {
      sent = new Set();
      this.sentToPeer.set(peerId, sent);
    }
    sent.add(id);
    return 'sent';
  }

  /** A peer declined a segment we asked it for: try other sources now. */
  private handlePeerNack(segmentId: string, fromPeerId: string): void {
    const id = canonicalSegmentId(segmentId);
    if (!SEGMENT_ID_REGEX.test(id)) return;
    this.emit('segment-nack', id, fromPeerId);
    if (this.role === 'seed') return;
    if (this.isSeed(fromPeerId)) this.seedOrigin?.onNack(id, fromPeerId);
    this.scheduler.retryPeersFor(id, fromPeerId);
  }

  /**
   * Seed first-hop fan-out cap: the current live-head segment is sent to at
   * most `seedFirstHopFanout` distinct peers; the rest get it from those peers
   * (or from the seed on its next publish). Older segments and init.mp4 are not capped.
   */
  private admitSeedUpload(segmentId: string, peerId: string): boolean {
    if (this.role !== 'seed' || !this.isLiveHeadSegment(segmentId)) return true;
    let served = this.seedLiveHeadServed.get(segmentId);
    if (!served) {
      served = new Set();
      this.seedLiveHeadServed.set(segmentId, served);
    }
    if (served.has(peerId)) return true;
    if (served.size >= this.config.seedFirstHopFanout) {
      this.emit('p2p-skip', 'seed fan-out cap', segmentId);
      return false;
    }
    served.add(peerId);
    return true;
  }

  private originFetcherCandidates(): string[] {
    return [this.peerId, ...this.viewerPeerIds()];
  }

  private electedFetcherId(segmentId: string): string {
    return this.scheduler.electFetcherPeerId(
      segmentId,
      this.originFetcherCandidates(),
    );
  }

  private ensureUploadFetch(segmentId: string): void {
    // The seed is the origin: what it has is published, nothing to fetch.
    if (this.role === 'seed') return;
    const id = canonicalSegmentId(segmentId);
    if (this.isStaleSegment(id)) {
      this.pendingUploads.delete(id);
      this.uploadFetchTriggered.delete(id);
      return;
    }
    const requesters = this.pendingUploads.get(id);
    if (!requesters || requesters.size === 0) return;

    const cached = this.getStoredSegmentForUpload(id);
    if (cached) {
      this.flushPendingUploads(id);
      return;
    }

    const sha256 = this.segmentMeta.get(id)?.sha256;
    if (!sha256) return;

    if (!this.config.transportProfile) {
      const fetcherId = this.electedFetcherId(id);
      if (fetcherId !== this.peerId) {
        this.peers.get(fetcherId)?.requestSegment(id, true);
        return;
      }
    }

    if (this.uploadFetchTriggered.has(id)) {
      if (this.getStoredSegmentForUpload(id)) {
        this.flushPendingUploads(id);
      }
      return;
    }
    this.uploadFetchTriggered.add(id);
    const epoch = this.catalogEpoch;

    void this.scheduler
      .ensureOriginForUpload(id, sha256)
      .finally(() => {
        if (epoch !== this.catalogEpoch) return;
        this.uploadFetchTriggered.delete(id);
        if (this.getStoredSegmentForUpload(id)) {
          this.flushPendingUploads(id);
        }
      }).catch(() => { /* Deadline/cancellation is handled by the scheduler; a relay pull is best effort. */ });
  }

  private segmentStoreKeysForUpload(segmentId: string): string[] {
    const id = canonicalSegmentId(segmentId);
    const base = segmentCacheKey(this.renditionId, id);
    const hash = this.segmentMeta.get(id)?.sha256?.toLowerCase();
    return hash ? [`${base}#${hash}`, base] : [base];
  }

  private getStoredSegmentForUpload(segmentId: string): ArrayBuffer | undefined {
    if (segmentId === INIT_SEGMENT_ID && this.initData) return this.initData;
    for (const key of this.segmentStoreKeysForUpload(segmentId)) {
      const data = this.store.get(key);
      if (data) return data;
    }
    return undefined;
  }

  private isLiveHeadSegment(segmentId: string): boolean {
    const seq = segmentSequence(segmentId);
    if (seq === null) return false;
    return seq >= this.highestSegmentSequence;
  }

  private queuePendingUpload(
    segmentId: string,
    requesterPeerId: string,
  ): void {
    let requesters = this.pendingUploads.get(segmentId);
    if (!requesters) {
      requesters = new Set();
      this.pendingUploads.set(segmentId, requesters);
    }
    if (requesters.has(requesterPeerId)) return;
    requesters.add(requesterPeerId);
    this.emit('pending-upload-queued', segmentId, requesterPeerId);
  }

  private flushPendingUploads(segmentId: string, attempt = 0): void {
    const id = canonicalSegmentId(segmentId);
    const requesters = this.pendingUploads.get(id);
    if (!requesters || requesters.size === 0) return;

    const data = this.getStoredSegmentForUpload(id);
    if (!data) return;

    let fulfilled = 0;
    const stillPending = new Set<string>();
    for (const requesterPeerId of requesters) {
      const outcome = this.serveUpload(id, requesterPeerId, data);
      if (outcome === 'sent') fulfilled++;
      else if (outcome === 'unavailable' && this.peers.has(requesterPeerId)) {
        stillPending.add(requesterPeerId);
      }
    }
    if (stillPending.size > 0) {
      this.pendingUploads.set(id, stillPending);
      if (attempt < 8) {
        setTimeout(
          () => this.flushPendingUploads(id, attempt + 1),
          40 * (attempt + 1),
        );
      }
    } else {
      this.pendingUploads.delete(id);
    }
    if (fulfilled > 0) {
      this.emit('pending-upload-fulfilled', id, fulfilled);
    }
    if (stillPending.size === 0) {
      this.uploadFetchTriggered.delete(id);
    }
  }

  private clearPendingUploadsForPeer(peerId: string): void {
    for (const [segmentId, requesters] of this.pendingUploads) {
      requesters.delete(peerId);
      if (requesters.size === 0) {
        this.pendingUploads.delete(segmentId);
      }
    }
  }

  private canConnect(id: string): boolean {
    if (!this.config.transportProfile) return true;
    if (this.role === 'viewer' && !this.isSeed(id) && !this.deliveryAssignment?.neighbors.includes(id)) return false;
    if (this.isSeed(id) && this.mediaOrigin === 'proxy') return false;
    if (this.peers.has(id)) return true;
    return this.peers.size < Math.min(8, this.config.maxPeers);
  }
  private ensureSeedConnection(): void {
    if (this.destroyed || this.mediaOrigin === 'proxy' || this.role === 'seed') return;
    const id = this.liveWindow?.seedPeerId || [...this.seedPeerIds][0];
    if (id && !this.peers.has(id) && this.canConnect(id)) void this.initiateConnection(id);
  }

  private shouldInitiateConnection(peerId: string): boolean {
    // The seed is a passive answerer: viewers always initiate to it; keep
    // lexicographic tie-breaking for viewer-viewer glare avoidance.
    if (this.role === 'seed') return false;
    return this.isSeed(peerId) || this.peerId > peerId;
  }

  private initiateConnection(peerId: string): Promise<void> {
    return this.trackWork(this.initiateConnectionOwned(peerId));
  }
  private async initiateConnectionOwned(peerId: string): Promise<void> {
    if (this.peers.has(peerId) || this.destroyed || !this.canConnect(peerId)) return;

    const conn = this.createPeerConnection(peerId);
    this.peers.set(peerId, conn);

    try {
      const offer = await conn.createOffer();
      if (this.destroyed || this.peers.get(peerId) !== conn) return;
      this.signalingClient.sendOffer(peerId, { sdp: offer.sdp ?? '' });
    } catch {
      this.removePeer(peerId);
      return;
    }
    this.watchOffer(peerId, conn);
  }

  /** See OFFER_TIMEOUT_MS. */
  private watchOffer(peerId: string, conn: PeerConnection): void {
    const existing = this.offerTimers.get(peerId);
    if (existing) clearTimeout(existing);
    this.offerTimers.set(
      peerId,
      setTimeout(() => {
        this.offerTimers.delete(peerId);
        if (this.destroyed || this.peers.get(peerId) !== conn || conn.isOpen()) return;
        this.removePeer(peerId);
        if (this.listedPeerIds.has(peerId) && this.shouldInitiateConnection(peerId)) {
          void this.initiateConnection(peerId);
        }
      }, OFFER_TIMEOUT_MS),
    );
  }

  private handleOffer(
    fromPeerId: string,
    payload: OfferPayload,
  ): Promise<void> {
    return this.trackWork(this.handleOfferOwned(fromPeerId, payload));
  }
  private async handleOfferOwned(
    fromPeerId: string,
    payload: OfferPayload,
  ): Promise<void> {
    if (this.destroyed || !this.canConnect(fromPeerId)) return;
    // A hidden seed answers nobody: no PeerConnection, no ICE candidate, no address.
    if (this.role === 'seed' && !this.config.acceptPeerConnections) return;

    let conn = this.peers.get(fromPeerId);
    // A repeated offer for a connection that never opened (our earlier answer was lost, see
    // OFFER_TIMEOUT_MS) gets a fresh connection: the old one is stuck mid-negotiation.
    if (conn && !conn.isOpen()) {
      this.removePeer(fromPeerId);
      conn = undefined;
    }
    if (!conn) {
      conn = this.createPeerConnection(fromPeerId);
      this.peers.set(fromPeerId, conn);
    }

    try {
      const answer = await conn.createAnswer(payload.sdp);
      if (this.destroyed || this.peers.get(fromPeerId) !== conn) return;
      this.signalingClient.sendAnswer(fromPeerId, { sdp: answer.sdp ?? '' });
    } catch {
      this.removePeer(fromPeerId);
    }
  }

  private handleAnswer(
    fromPeerId: string,
    payload: AnswerPayload,
  ): Promise<void> {
    return this.trackWork(this.handleAnswerOwned(fromPeerId, payload));
  }
  private async handleAnswerOwned(
    fromPeerId: string,
    payload: AnswerPayload,
  ): Promise<void> {
    const conn = this.peers.get(fromPeerId);
    if (!conn) return;
    try {
      await conn.acceptAnswer(payload.sdp);
    } catch {
      this.removePeer(fromPeerId);
    }
  }

  private handleIceCandidate(
    fromPeerId: string,
    payload: IceCandidatePayload,
  ): Promise<void> {
    return this.trackWork(this.handleIceCandidateOwned(fromPeerId, payload));
  }
  private async handleIceCandidateOwned(
    fromPeerId: string,
    payload: IceCandidatePayload,
  ): Promise<void> {
    const conn = this.peers.get(fromPeerId);
    if (!conn) return;
    try {
      await conn.addIceCandidate(payload);
    } catch {
      // Non-fatal
    }
  }

  private resolveIcePolicy(): RTCIceTransportPolicy {
    const threshold = this.config.iceTransportPolicyThreshold;
    if (threshold !== undefined) {
      return this.swarmPeerCount < threshold ? 'relay' : 'all';
    }
    return this.config.iceTransportPolicy;
  }

  private createPeerConnection(peerId: string): PeerConnection {
    // An empty STUN list means "host candidates only" (LAN, loopback e2e); RTCPeerConnection
    // rejects an entry with no URLs.
    const iceServers: RTCIceServer[] = [
      ...(this.config.stunUrls.length > 0 ? [{ urls: this.config.stunUrls }] : []),
      ...this.config.turnServers,
    ];

    const conn = new PeerConnection(
      peerId,
      iceServers,
      this.resolveIcePolicy(),
      (pid, candidate) => {
        this.signalingClient.sendIceCandidate(pid, candidate);
      },
      (segmentId, data, fromPeerId) => {
        if (this.role === 'seed') return;
        void this.trackWork(this.scheduler.handlePeerSegment(segmentId, data, fromPeerId));
      },
      (segmentId, fromPeerId) => {
        this.handleSegmentUploadRequest(segmentId, fromPeerId);
      },
      (pid, state) => {
        if (state === 'connected') {
          this.metrics.recordPeerConnected();
          if (this.isSeed(pid)) {
            this.seedOrigin?.notifySeedAvailable();
          } else {
            this.scheduler.onPeerConnected(pid);
          }
          this.emit('peer-connected', pid);
        } else if (
          state === 'failed' ||
          state === 'closed' ||
          state === 'disconnected'
        ) {
          this.removePeer(pid);
        }
      },
    );

    conn.onWorkStarted = work => { void this.trackWork(work); };
    conn.uploadDeadlineMs = this.config.transportProfile ? Math.min(600, this.config.p2pDeadlineMs) : 1000;
    conn.onUploadMeasured = observation => this.uploadCapacity.record(observation);
    conn.onPayloadReceived = bytes => {
      this.metrics.recordNetworkBytes(this.isSeed(peerId) ? 'seed' : 'peer', conn.transportPath, bytes);
    };
    conn.onPartialReceiveCanceled = bytes => this.metrics.recordNetworkWaste('canceled', bytes);
    conn.onNack = (segmentId, fromPeerId) => {
      this.handlePeerNack(segmentId, fromPeerId);
    };
    conn.onIceRestartRequest = () => {
      if (this.destroyed || this.peers.get(peerId) !== conn) return;
      void this.trackWork(conn.restartIce().then(offer => {
        if (offer && !this.destroyed && this.peers.get(peerId) === conn) this.signalingClient.sendOffer(peerId, { sdp: offer.sdp ?? '' });
      }).catch(() => { /* Keep existing channels; the next bounded lease renewal may retry. */ }));
    };

    conn.onDataChannelReady = () => {
      if (this.isSeed(peerId)) {
        this.seedOrigin?.notifySeedAvailable();
      } else {
        this.scheduler.onPeerDataChannelReady();
      }
    };

    return conn;
  }

  private removePeer(peerId: string): void {
    const timer = this.offerTimers.get(peerId);
    if (timer) {
      clearTimeout(timer);
      this.offerTimers.delete(peerId);
    }
    const conn = this.peers.get(peerId);
    if (!conn) return;
    this.bytesUploaded += conn.uploadSnapshot?.bytesSent ?? 0;
    this.confirmedUploadBytes += conn.uploadSnapshot?.confirmedBytes ?? 0;
    this.confirmedUploadDeliveries += conn.uploadSnapshot?.confirmedDeliveries ?? 0;
    conn.destroy();
    this.peers.delete(peerId);
    this.sentToPeer.delete(peerId);
    for (const peers of this.seedDeferred.values()) peers.delete(peerId);
    this.clearPendingUploadsForPeer(peerId);
    this.scheduler.removePeer(peerId);
    this.metrics.recordPeerDisconnected();
    this.emit('peer-disconnected', peerId);
  }

  private disconnectAllPeers(): void {
    for (const peerId of Array.from(this.peers.keys())) {
      this.removePeer(peerId);
    }
  }

  private emit<K extends keyof P2PEngineEventMap>(
    event: K,
    ...args: Parameters<P2PEngineEventMap[K]>
  ): void {
    if(this.forwardOwner)this.forwardOwner.emit(event,...args);
    const list = this.eventHandlers[event];
    if (!list) return;
    for (const cb of list) {
      (cb as (...a: unknown[]) => void)(...(args as unknown[]));
    }
  }

  private generatePeerId(): string {
    let id: string;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      id = crypto.randomUUID();
    } else {
      const arr = new Uint8Array(16);
      crypto.getRandomValues(arr);
      id = Array.from(arr)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    }
    return this.role === 'seed' ? `seed-${id}` : id;
  }

  private detectRegion(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'unknown';
    }
  }
}
